import { useState } from 'react';
import { GeneralSignInInfoResult } from '@mews-commander/data-access';
import { DataTestId, FeatureFlag, TextKey } from '@mews-commander/enums';
import { Link, OverlayedLoader } from '@mews-commander/ui';
import { CaptchaChallenge, LocalizedText, useFeatureFlag } from '@mews/framework';
import { Button, Link as OptimusLink, Stack } from '@optimus-web/core';
import { useCustomDocumentTitle } from '../../../../../../layouts/useCustomDocumentTitle';
import { AutofocusedInputField, AutofocusedPasswordField } from '../../../../components/AutofocusedField';
import { FormStep, SignInEventHandlers } from '../useSignInRouter';
import { useHasPasskeySupport } from '../useSignInWithPasskey';
import { FormField, useSignInCredentialsForm } from './useSignInCredentialsForm';

interface SingInCredentialsProps extends SignInEventHandlers {
    step: Exclude<FormStep, '2fa'>;
    signInInfo: GeneralSignInInfoResult | undefined;
    isCheckingPasskey: boolean;
    onPasskeyRetry: () => void;
}

export const SignInCredentials = ({
    step,
    onEmailSubmit,
    onPasswordReset,
    onTwoFactorCheck,
    onPasskeyRetry,
    signInInfo,
    isCheckingPasskey,
}: SingInCredentialsProps) => {
    const captchaEnabled = useFeatureFlag(FeatureFlag.captchaOnSignInEnabled);
    useCustomDocumentTitle({ textKey: TextKey.SignIn });
    const [captchaToken, setCaptchaToken] = useState('');

    const passkeysEnabled = useHasPasskeySupport();

    const handleEmailChange = () => {
        if (step !== 'credentials') {
            return;
        }

        resetField(FormField.Password);
        onPasswordReset();
    };

    const { renderFormContent, resetField, isSubmitting } = useSignInCredentialsForm({
        step,
        onEmailSubmit,
        onTwoFactorCheck,
        getCaptchaToken: () => captchaToken,
    });

    return (
        <Stack spacing="200" vertical>
            {/* We want to keep the spinner visible while the user is being redirected to the SSO login page */}
            <OverlayedLoader show={isSubmitting || signInInfo?.ssoInfo?.enforceSingleSignOn || isCheckingPasskey} />
            {renderFormContent(
                <Stack vertical spacing="200">
                    <AutofocusedInputField
                        key="nameOrEmail"
                        label={<LocalizedText textKey={TextKey.UserNameOrEmail} />}
                        name={FormField.NameOrEmail}
                        autoComplete="username"
                        onChange={handleEmailChange}
                        data-test-id={DataTestId.SignInEmail}
                    />
                    {/**
                     * This would be cleaner if we had a separate component for the password field
                     * Sadly, doing this creates a weird UX when editing the email
                     * If separated, the email input component would be a different one, resetting the cursor position
                     */}
                    {step === 'credentials' && (
                        <>
                            <AutofocusedPasswordField
                                label={<LocalizedText textKey={TextKey.Password} />}
                                name={FormField.Password}
                                autoComplete="current-password"
                                data-test-id={DataTestId.SignInPassword}
                            />
                            <Link to="/Home/ResetPassword">
                                <LocalizedText textKey={TextKey.ForgotPasswordQuestion} />
                            </Link>
                        </>
                    )}

                    <Button
                        type="submit"
                        variant="primary"
                        wide
                        disabled={isSubmitting || isCheckingPasskey}
                        data-test-id={DataTestId.FormMainButton}
                    >
                        <LocalizedText textKey={TextKey.Continue} />
                    </Button>
                </Stack>
            )}

            {signInInfo?.ssoInfo?.signInUrl && !signInInfo?.ssoInfo?.enforceSingleSignOn && (
                <Stack alignment="center">
                    <Link to={signInInfo?.ssoInfo?.signInUrl} data-test-id={DataTestId.SignInSsoLink} inherit>
                        <LocalizedText textKey={TextKey.LoginWithSso} />
                    </Link>
                </Stack>
            )}

            {passkeysEnabled && signInInfo?.passkeyInfo && (
                <Stack alignment="center">
                    <OptimusLink
                        as="button"
                        onClick={onPasskeyRetry}
                        data-test-id={DataTestId.SignInPasskeyLink}
                        inherit
                    >
                        <LocalizedText textKey={TextKey.LoginWithPasskey} />
                    </OptimusLink>
                </Stack>
            )}

            {captchaEnabled ? (
                <Stack alignment="center" spacing="0">
                    <CaptchaChallenge onSuccess={setCaptchaToken} challengeLabel="commander-login" />
                </Stack>
            ) : null}
        </Stack>
    );
};
